/**
 * Cart sync — makes third-party cart mutations refresh the theme cart UI.
 *
 * Free-gift and upsell apps add items with their own AJAX calls to /cart/add.js,
 * which fire none of the theme's `cart:update` events, so the cart drawer and
 * header bubble go stale until refresh. This shim observes successful non-theme
 * POSTs to the cart mutation endpoints (via fetch and XHR patches), fetches
 * /cart.js, and dispatches the theme's own `cart:update` event so every
 * existing listener (cart-items-component, cart-icon, product forms) refreshes
 * through its normal code path.
 *
 * Classic script — MUST load before {{ content_for_header }} in
 * layout/theme.liquid so app embeds capture the patched fetch/XHR.
 */
(function () {
	'use strict';

	var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;
	var CART_UPDATE_ONLY_RE = /\/cart\/update(\.js|\.json)?$/;
	var DEBOUNCE_MS = 250;
	var SOURCE_ID = 'cart-sync';

	// When false, an externally added item updates the drawer contents and bubble
	// in place without popping the drawer open (it is usually already open from
	// the customer's own add-to-cart). Set to true to auto-open on external adds.
	var OPEN_DRAWER_ON_EXTERNAL_ADD = false;

	/** @type {ReturnType<typeof setTimeout> | null} */
	var timer = null;

	var lastExternalMutationAt = 0;
	var lastThemeMutationSentAt = 0;

	/**
	 * Whether a request is a cart mutation (POST to /cart/add|change|update|clear).
	 * @param {string} method
	 * @param {string | URL} url
	 * @returns {boolean}
	 */
	function isCartMutation(method, url) {
		if (!method || String(method).toUpperCase() !== 'POST') return false;
		try {
			return CART_MUTATION_RE.test(new URL(url, window.location.href).pathname);
		} catch (error) {
			return false;
		}
	}

	/**
	 * Whether a request body carries the Section Rendering API `sections` param —
	 * the signature of the theme's own cart writers (product-form,
	 * component-cart-items, cart-discount, quick-order-list). Those already
	 * dispatch `cart:update` themselves, so the shim must not double-render.
	 * @param {*} body
	 * @returns {boolean}
	 */
	function isThemeOriginated(body) {
		try {
			if (body instanceof FormData || body instanceof URLSearchParams) return body.has('sections');
			if (typeof body === 'string') {
				return body.indexOf('"sections"') !== -1 || body.indexOf('sections=') !== -1;
			}
		} catch (error) {
			// Uninspectable body — treat as external; a redundant morph is benign.
		}
		return false;
	}

	/**
	 * Whether a /cart/update call touches only `note`/`attributes` (e.g. the
	 * theme's cart-note saves) — such calls cannot change line items and
	 * re-rendering on them would clobber the note textarea mid-typing.
	 * @param {string | URL} url
	 * @param {*} body
	 * @returns {boolean}
	 */
	function isNoteOrAttributesOnly(url, body) {
		try {
			if (!CART_UPDATE_ONLY_RE.test(new URL(url, window.location.href).pathname)) return false;
			if (typeof body !== 'string') return false;
			var parsed = JSON.parse(body);
			if (!parsed || typeof parsed !== 'object') return false;
			return Object.keys(parsed).every(function (key) {
				return key === 'note' || key === 'attributes';
			});
		} catch (error) {
			return false;
		}
	}

	function classify(method, url, body) {
		if (!isCartMutation(method, url) || isNoteOrAttributesOnly(url, body)) return 'ignore';
		return isThemeOriginated(body) ? 'theme' : 'external';
	}

	function themeRenderCoversLastExternalMutation() {
		return lastThemeMutationSentAt > lastExternalMutationAt;
	}

	/**
	 * Schedules a debounced refresh, coalescing rapid app calls (gift apps often
	 * add then update within a few hundred ms) into a single render.
	 */
	function scheduleRefresh() {
		if (timer !== null) clearTimeout(timer);
		timer = setTimeout(refresh, DEBOUNCE_MS);
	}

	/**
	 * Cancels a pending synthetic refresh (a genuine theme event already
	 * rendered the cart).
	 */
	function cancelRefresh() {
		if (timer === null) return;
		clearTimeout(timer);
		timer = null;
	}

	/**
	 * Fetches the current cart and dispatches the theme's `cart:update` event.
	 */
	function refresh() {
		timer = null;

		var cartUrl;
		try {
			cartUrl = Theme.routes.cart_url;
		} catch (error) {
			cartUrl = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart';
		}

		originalFetch
			.call(window, cartUrl + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
			.then(function (response) {
				if (!response.ok) throw new Error('cart-sync: /cart.js returned ' + response.status);
				return response.json();
			})
			.then(dispatchCartUpdate)
			.catch(function () {
				// Never surface shim errors to the page.
			});
	}

	/**
	 * Dispatches a synthetic `cart:update` (see ThemeEvents.cartUpdate,
	 * assets/events.js) matching the CartUpdateEvent detail contract:
	 * - `detail.data` must be an object (component-cart-items reads
	 *   `detail.data.sections` without optional chaining on `data`)
	 * - no `data.sections` key → cart-items-component falls back to a full
	 *   Section Rendering API re-render of drawer and cart page
	 * - absolute `itemCount` with a source other than 'product-form-component'
	 *   → cart-icon sets the bubble count instead of adding a delta
	 * @param {Object} cart - The /cart.js JSON.
	 */
	function dispatchCartUpdate(cart) {
		// cart-drawer-component auto-opens on ANY `cart:update`; suppress that for
		// synthetic events so background app activity doesn't pop the drawer.
		var suppressedDrawers = [];
		if (!OPEN_DRAWER_ON_EXTERNAL_ADD) {
			suppressedDrawers = Array.prototype.slice.call(document.querySelectorAll('cart-drawer-component[auto-open]'));
			suppressedDrawers.forEach(function (drawer) {
				drawer.removeAttribute('auto-open');
			});
		}

		try {
			document.dispatchEvent(
				new CustomEvent('cart:update', {
					bubbles: true,
					detail: {
						resource: cart,
						sourceId: SOURCE_ID,
						data: { source: SOURCE_ID, itemCount: cart.item_count },
					},
				})
			);
		} finally {
			suppressedDrawers.forEach(function (drawer) {
				drawer.setAttribute('auto-open', '');
			});
		}
	}

	// Safety net: if a genuine theme event lands while a synthetic refresh is
	// pending, the cart UI is already up to date — drop the pending refresh.
	document.addEventListener('cart:update', function (event) {
		if (!event || !event.detail || event.detail.sourceId === SOURCE_ID) return;
		if (themeRenderCoversLastExternalMutation()) cancelRefresh();
	});
	document.addEventListener('discount:update', function () {
		if (themeRenderCoversLastExternalMutation()) cancelRefresh();
	});

	// --- fetch patch -----------------------------------------------------------
	var originalFetch = window.fetch;

	window.fetch = function (input, init) {
		var result = originalFetch.apply(this, arguments);
		try {
			var isRequest = typeof Request !== 'undefined' && input instanceof Request;
			var url = isRequest ? input.url : input;
			var method = (init && init.method) || (isRequest ? input.method : 'GET');
			// Request-object bodies are streams we must not consume; leaving `body`
			// undefined treats them as external, which at worst re-renders once.
			var body = init && init.body;

			var kind = classify(method, url, body);

			if (kind === 'theme') {
				lastThemeMutationSentAt = Date.now();
			} else if (kind === 'external') {
				result.then(
					function (response) {
						if (!response || !response.ok) return;
						lastExternalMutationAt = Date.now();
						scheduleRefresh();
					},
					function () {
						// Request failed — nothing to sync.
					}
				);
			}
		} catch (error) {
			// Observation must never interfere with the caller's request.
		}
		return result;
	};

	// --- XHR patch -------------------------------------------------------------
	var originalOpen = XMLHttpRequest.prototype.open;
	var originalSend = XMLHttpRequest.prototype.send;

	XMLHttpRequest.prototype.open = function (method, url) {
		try {
			this._cartSyncRequest = { method: method, url: url };
		} catch (error) {
			// Observation must never interfere with the caller's request.
		}
		return originalOpen.apply(this, arguments);
	};

	XMLHttpRequest.prototype.send = function (body) {
		try {
			var request = this._cartSyncRequest;
			var kind = request ? classify(request.method, request.url, body) : 'ignore';

			if (kind === 'theme') {
				lastThemeMutationSentAt = Date.now();
			} else if (kind === 'external') {
				this.addEventListener('loadend', function () {
					if (this.status < 200 || this.status >= 300) return;
					lastExternalMutationAt = Date.now();
					scheduleRefresh();
				});
			}
		} catch (error) {
			// Observation must never interfere with the caller's request.
		}
		return originalSend.apply(this, arguments);
	};
})();
